import { cn } from "@breakout/ui/utils"
import { motion } from "motion/react"
import type { Key } from "react"
import { useId } from "react"

interface Tab<T extends Key> {
	id: T
	label: string | React.ReactNode
}

interface AnimatedTabsProps<T extends Key> {
	tabs: Tab<T>[]
	activeTab: T
	onTabChange: (id: T) => void
	className?: string
}

export function AnimatedTabs<T extends Key>({
	tabs,
	activeTab,
	onTabChange,
	className = "",
}: AnimatedTabsProps<T>) {
	const layoutId = useId()

	return (
		<div className={cn("flex gap-4 sm:gap-6", className)} role="tablist">
			{tabs.map(tab => {
				const isActive = tab.id === activeTab
				return (
					<button
						aria-controls={`tabpanel-${String(tab.id)}`}
						aria-selected={isActive}
						className={`relative min-h-[44px] cursor-pointer rounded-lg px-2 pb-3 font-medium transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-breakout-brand active:scale-[0.98] ${
							isActive
								? "text-text-primary"
								: "text-text-muted hover:bg-bg-secondary/50 hover:text-text-secondary dark:text-white/50 dark:hover:text-white"
						}`}
						id={`tab-${String(tab.id)}`}
						key={String(tab.id)}
						onClick={() => onTabChange(tab.id)}
						role="tab"
						type="button"
					>
						{tab.label}
						{isActive && (
							<motion.div
								className="absolute right-0 bottom-0 left-0 h-0.5 bg-text-primary"
								layoutId={layoutId}
								transition={{
									type: "spring",
									stiffness: 500,
									damping: 30,
								}}
							/>
						)}
					</button>
				)
			})}
		</div>
	)
}
